import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "../utils/axios";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import ReviewModal from "../components/ReviewModal";

const CustomerOrdersPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewProduct, setReviewProduct] = useState(null);

  // Redirect if not customer
  useEffect(() => {
    if (isAuthenticated && user?.role !== "customer") {
      navigate("/");
      toast.error("This page is only available for customers");
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch customer orders
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
  } = useQuery(
    "customer-orders",
    async () => {
      const response = await axios.get("/api/orders/customer");
      return response.data;
    },
    {
      enabled: isAuthenticated && user?.role === "customer",
      refetchOnWindowFocus: false,
    }
  );

  // Fetch products for order creation
  const { data: productsData } = useQuery(
    "products-for-orders",
    async () => {
      const response = await axios.get("/api/products");
      return response.data;
    },
    {
      enabled: showAddOrderModal,
      refetchOnWindowFocus: false,
    }
  );

  const orders = ordersData?.orders || [];
  const products = productsData?.products || [];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "shipped":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch review eligibility for an order
  const { data: reviewEligibilityData } = useQuery(
    ["review-eligibility", selectedOrder?.id],
    async () => {
      if (!selectedOrder) return null;
      const response = await axios.get(
        `/api/reviews/can-review/${selectedOrder.id}`
      );
      return response.data;
    },
    {
      enabled: !!selectedOrder && showOrderDetails,
      refetchOnWindowFocus: false,
    }
  );

  const handleWriteReview = (product) => {
    setReviewProduct({
      ...product,
      order_date: selectedOrder.created_at,
    });
    setShowReviewModal(true);
  };

  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;

    const canReview = reviewEligibilityData?.canReview;
    const products = reviewEligibilityData?.products || [];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Order Details - #{order.id}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Order Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Order Date</p>
                <p className="font-medium">{formatDate(order.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    order.status
                  )}`}
                >
                  {order.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="font-bold text-lg text-green-600">
                  ₹{parseFloat(order.total_amount || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Status</p>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    order.payment_status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {order.payment_status || "Pending"}
                </span>
              </div>
            </div>

            {/* Cancellation Reason */}
            {order.status === "cancelled" && order.cancellation_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-red-800 mb-2">
                  Order Cancellation
                </h3>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      <strong>Reason:</strong> {order.cancellation_reason}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Shipping Address */}
            {order.shipping_address && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Shipping Address
                </h3>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  {order.shipping_address}
                </p>
              </div>
            )}

            {/* Order Items */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Order Items
              </h3>
              <div className="space-y-3">
                {products.length > 0
                  ? products.map((product, index) => {
                      const hasReview = product.review_id !== null;
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded"
                        >
                          <div className="flex-1">
                            <p className="font-medium">
                              {product.product_name}
                            </p>
                            {hasReview && (
                              <div className="flex items-center mt-1">
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <svg
                                      key={i}
                                      className={`w-4 h-4 ${
                                        i < product.review_rating
                                          ? "text-yellow-400 fill-current"
                                          : "text-gray-300"
                                      }`}
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  ))}
                                </div>
                                <span className="text-xs text-gray-600 ml-2">
                                  Your review
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            {canReview && (
                              <button
                                onClick={() => handleWriteReview(product)}
                                className={`text-sm font-medium ${
                                  hasReview
                                    ? "text-blue-600 hover:text-blue-700"
                                    : "text-green-600 hover:text-green-700"
                                }`}
                              >
                                {hasReview ? "Edit Review" : "Write Review"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  : order.items?.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded"
                      >
                        <div>
                          <p className="font-medium">
                            {item.product_name || `Product #${item.product_id}`}
                          </p>
                          <p className="text-sm text-gray-600">
                            Quantity: {item.quantity}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            ₹{parseFloat(item.price || 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-600">each</p>
                        </div>
                      </div>
                    ))}
              </div>
            </div>

            {/* Payment Image */}
            {order.payment_image && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Payment Proof
                </h3>
                <div className="bg-gray-50 p-3 rounded">
                  <img
                    src={order.payment_image}
                    alt="Payment proof"
                    className="max-w-full h-auto max-h-64 mx-auto rounded"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                  <p
                    className="text-sm text-gray-500 text-center mt-2"
                    style={{ display: "none" }}
                  >
                    Payment image could not be loaded
                  </p>
                </div>
              </div>
            )}

            {/* External Payment Details */}
            {order.external_payment_details && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  External Payment Details
                </h3>
                <div className="bg-gray-50 p-3 rounded">
                  <pre className="text-sm text-gray-600 whitespace-pre-wrap">
                    {order.external_payment_details}
                  </pre>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button onClick={onClose} className="btn-secondary btn-md">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AddOrderModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
      product_id: "",
      quantity: 1,
      external_payment_details: "",
      payment_image: null,
      shipping_address: "",
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);

    const addOrderMutation = useMutation(
      async (orderData) => {
        const formDataToSend = new FormData();
        Object.keys(orderData).forEach((key) => {
          if (orderData[key] !== null && orderData[key] !== "") {
            formDataToSend.append(key, orderData[key]);
          }
        });

        const response = await axios.post(
          "/api/orders/external",
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries("customer-orders");
          toast.success("Order created successfully!");
          onClose();
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message || "Failed to create order"
          );
        },
      }
    );

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!formData.product_id) {
        toast.error("Please select a product");
        return;
      }
      setUploading(true);
      addOrderMutation.mutate(formData);
    };

    const handleImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          // 5MB limit
          toast.error("Image size should be less than 5MB");
          return;
        }
        setFormData((prev) => ({ ...prev, payment_image: file }));
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
      }
    };

    const selectedProduct = products.find(
      (p) => p.id === parseInt(formData.product_id)
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Add New Order
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Product Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Product *
              </label>
              <select
                value={formData.product_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    product_id: e.target.value,
                  }))
                }
                className="input"
                required
              >
                <option value="">Choose a product...</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} - ₹{product.price}
                  </option>
                ))}
              </select>
            </div>

            {/* Product Details */}
            {selectedProduct && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900">
                  {selectedProduct.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedProduct.description}
                </p>
                <p className="text-lg font-bold text-green-600 mt-2">
                  ₹{selectedProduct.price}
                </p>
              </div>
            )}

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity *
              </label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantity: parseInt(e.target.value) || 1,
                  }))
                }
                className="input"
                required
              />
            </div>

            {/* Total Amount */}
            {selectedProduct && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-blue-600">
                  ₹
                  {(
                    parseFloat(selectedProduct.price) * formData.quantity
                  ).toFixed(2)}
                </p>
              </div>
            )}

            {/* External Payment Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                External Payment Details
              </label>
              <textarea
                value={formData.external_payment_details}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    external_payment_details: e.target.value,
                  }))
                }
                className="input"
                rows="4"
                placeholder="Enter payment details like UPI ID, bank transfer details, payment gateway transaction ID, etc."
              />
            </div>

            {/* Payment Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Proof Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload payment screenshot or receipt (Max 5MB)
              </p>

              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview}
                    alt="Payment proof preview"
                    className="max-w-full h-auto max-h-64 mx-auto rounded border"
                  />
                </div>
              )}
            </div>

            {/* Shipping Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shipping Address
              </label>
              <textarea
                value={formData.shipping_address}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    shipping_address: e.target.value,
                  }))
                }
                className="input"
                rows="3"
                placeholder="Enter your complete shipping address..."
              />
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary btn-md"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary btn-md"
                disabled={uploading || addOrderMutation.isLoading}
              >
                {uploading || addOrderMutation.isLoading ? (
                  <div className="flex items-center">
                    <div className="loading-dots mr-2">
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                    Creating Order...
                  </div>
                ) : (
                  "Create Order"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (!isAuthenticated || user?.role !== "customer") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-6">
            This page is only available for customers.
          </p>
          <button onClick={() => navigate("/")} className="btn-primary btn-md">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
              <p className="mt-1 text-sm text-gray-600">
                View and manage your orders
              </p>
            </div>
            <button
              onClick={() => setShowAddOrderModal(true)}
              className="btn-primary btn-lg"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add New Order
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Orders List */}
        {ordersLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="loading-dots">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        ) : ordersError ? (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Orders
            </h3>
            <p className="text-gray-600 mb-4">
              We couldn't load your orders. Please try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary btn-md"
            >
              Retry
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              No Orders Yet
            </h3>
            <p className="mt-1 text-sm text-gray-500 mb-6">
              You haven't placed any orders yet. Start shopping to see your
              orders here.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => setShowAddOrderModal(true)}
                className="btn-primary btn-lg mr-4"
              >
                Add New Order
              </button>
              <button
                onClick={() => navigate("/products")}
                className="btn-secondary btn-lg"
              >
                Browse Products
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-6">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Order #{order.id}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {order.status}
                      </span>
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                        }}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                      >
                        View Details
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Total Amount</p>
                      <p className="font-bold text-lg text-green-600">
                        ₹{parseFloat(order.total_amount || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Items</p>
                      <p className="font-medium">
                        {order.items ? order.items.length : 0} item(s)
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Payment Status</p>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          order.payment_status === "completed"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {order.payment_status || "Pending"}
                      </span>
                    </div>
                  </div>

                  {order.shipping_address && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600">Shipping to:</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {order.shipping_address}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddOrderModal && (
        <AddOrderModal onClose={() => setShowAddOrderModal(false)} />
      )}

      {showOrderDetails && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
        />
      )}

      {showReviewModal && reviewProduct && (
        <ReviewModal
          product={reviewProduct}
          orderId={selectedOrder.id}
          existingReview={
            reviewProduct.review_id
              ? {
                  id: reviewProduct.review_id,
                  rating: reviewProduct.review_rating,
                  review_text: reviewProduct.review_text,
                }
              : null
          }
          onClose={() => {
            setShowReviewModal(false);
            setReviewProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default CustomerOrdersPage;
